/**
 * AuthZEN Response Validation
 * 
 * Validates PDP responses per OpenID AuthZEN 1.0 Draft 4
 * 
 * Spec Sections:
 * - Section 5.5: Decision structure
 * - Section 6.2: Access Evaluation Response
 * - Section 7.2: Access Evaluations Response
 */

export interface AuthZENDecision {
	decision: boolean;
	context?: {
		id?: string;
		reason_admin?: Record<string, string>;
		reason_user?: Record<string, string>;
		obligations?: any[];
		advice?: any[];
		constraints?: any[];
		[key: string]: any;
	};
}

export interface BatchEvaluationResponse {
	evaluations: AuthZENDecision[];
}

export class AuthZENValidationError extends Error {
	constructor(
		message: string,
		public readonly field?: string,
		public readonly receivedValue?: any
	) {
		super(message);
		this.name = 'AuthZENValidationError';
	}
}

export class AuthZENResponseValidator {
	/**
	 * Validate single evaluation response
	 * 
	 * Per OpenID AuthZEN 1.0 Draft 4:
	 * - Section 5.5: Decision MUST have 'decision' (boolean) field
	 * - Section 5.5: Decision MAY have 'context' (object) field
	 * - Section 6.2: Response is a Decision object
	 * 
	 * @throws {AuthZENValidationError} If response is invalid
	 * @returns {AuthZENDecision} Validated decision
	 * 
	 * @example
	 * ```typescript
	 * const data = await response.json();
	 * const decision = AuthZENResponseValidator.validateEvaluationResponse(data);
	 * // decision.decision is guaranteed to be boolean
	 * ```
	 */
	static validateEvaluationResponse(data: any): AuthZENDecision {
		// Validate response is an object
		if (!data || typeof data !== 'object') {
			throw new AuthZENValidationError(
				'Invalid PDP response: expected object, got ' + typeof data,
				'response',
				data
			);
		}
		
		// Validate required 'decision' field exists
		if (!('decision' in data)) {
			throw new AuthZENValidationError(
				'Invalid PDP response: missing required field "decision" (OpenID AuthZEN Section 5.5)',
				'decision',
				data
			);
		}
		
		// Validate 'decision' is boolean
		if (typeof data.decision !== 'boolean') {
			throw new AuthZENValidationError(
				`Invalid PDP response: decision must be boolean per spec, got ${typeof data.decision}`,
				'decision',
				data.decision
			);
		}
		
		// Validate optional 'context' field if present
		if ('context' in data && data.context !== null && data.context !== undefined) {
			if (typeof data.context !== 'object') {
				throw new AuthZENValidationError(
					`Invalid PDP response: context must be object if present, got ${typeof data.context}`,
					'context',
					data.context
				);
			}
		}
		
		// Check for deprecated 'allowed' field (backwards compatibility warning)
		if ('allowed' in data) {
			console.warn(
				'[AuthZ] Server response contains deprecated "allowed" field. ' +
				'The OpenID AuthZEN 1.0 spec uses "decision" instead. ' +
				'Please update your PDP to use the standard "decision" field.'
			);
		}
		
		return data as AuthZENDecision;
	}
	
	/**
	 * Validate batch evaluation response
	 * 
	 * Per OpenID AuthZEN 1.0 Draft 4 Section 7.2:
	 * - Response MUST have 'evaluations' array
	 * - Each element in array MUST be a valid Decision
	 * 
	 * @throws {AuthZENValidationError} If response is invalid
	 * @returns {BatchEvaluationResponse} Validated batch response
	 */
	static validateBatchResponse(data: any): BatchEvaluationResponse {
		// Validate response is an object
		if (!data || typeof data !== 'object') {
			throw new AuthZENValidationError(
				'Invalid batch response: expected object, got ' + typeof data,
				'response',
				data
			);
		}
		
		// Validate required 'evaluations' field exists
		if (!('evaluations' in data)) {
			throw new AuthZENValidationError(
				'Invalid batch response: missing required field "evaluations" (OpenID AuthZEN Section 7.2)',
				'evaluations',
				data
			);
		}
		
		// Validate 'evaluations' is an array
		if (!Array.isArray(data.evaluations)) {
			throw new AuthZENValidationError(
				`Invalid batch response: evaluations must be array, got ${typeof data.evaluations}`,
				'evaluations',
				data.evaluations
			);
		}
		
		// Validate each decision in the array
		data.evaluations.forEach((decision: any, index: number) => {
			try {
				this.validateEvaluationResponse(decision);
			} catch (error) {
				if (error instanceof AuthZENValidationError) {
					throw new AuthZENValidationError(
						`Invalid decision at index ${index}: ${error.message}`,
						`evaluations[${index}].${error.field}`,
						error.receivedValue
					);
				}
				throw error;
			}
		});
		
		return data as BatchEvaluationResponse;
	}
	
	/**
	 * Validate search response
	 * 
	 * Per OpenID AuthZEN 1.0 Draft 4 Section 8.3:
	 * - Response MUST have 'results' array
	 * - Response MAY have 'page' object
	 * - Response MAY have 'context' object
	 */
	static validateSearchResponse(data: any): {
		results: any[];
		page?: any;
		context?: any;
	} {
		if (!data || typeof data !== 'object') {
			throw new AuthZENValidationError(
				'Invalid search response: expected object',
				'response',
				data
			);
		}
		
		if (!('results' in data)) {
			throw new AuthZENValidationError(
				'Invalid search response: missing required field "results"',
				'results',
				data
			);
		}
		
		if (!Array.isArray(data.results)) {
			throw new AuthZENValidationError(
				`Invalid search response: results must be array, got ${typeof data.results}`,
				'results',
				data.results
			);
		}
		
		// Validate optional page object
		if ('page' in data && data.page !== null && typeof data.page !== 'object') {
			throw new AuthZENValidationError(
				'Invalid search response: page must be object if present',
				'page',
				data.page
			);
		}
		
		return data;
	}
	
	/**
	 * Validate metadata discovery response
	 * 
	 * Per OpenID AuthZEN 1.0 Draft 4 Section 9.1:
	 * - MUST have 'policy_decision_point' field
	 * - MUST have 'access_evaluation_endpoint' field
	 * - All endpoint URLs MUST be absolute
	 */
	static validateMetadataResponse(data: any): {
		policy_decision_point: string;
		access_evaluation_endpoint: string;
		access_evaluations_endpoint?: string;
		search_subject_endpoint?: string;
		search_resource_endpoint?: string;
		search_action_endpoint?: string;
		capabilities?: string[];
		[key: string]: any;
	} {
		if (!data || typeof data !== 'object') {
			throw new AuthZENValidationError(
				'Invalid metadata response: expected object',
				'response',
				data
			);
		}
		
		// Validate required fields
		if (!data.policy_decision_point) {
			throw new AuthZENValidationError(
				'Invalid metadata: missing required field "policy_decision_point"',
				'policy_decision_point',
				data
			);
		}
		
		if (!data.access_evaluation_endpoint) {
			throw new AuthZENValidationError(
				'Invalid metadata: missing required field "access_evaluation_endpoint"',
				'access_evaluation_endpoint',
				data
			);
		}
		
		// Validate URLs are absolute
		const urlFields = [
			'policy_decision_point',
			'access_evaluation_endpoint',
			'access_evaluations_endpoint',
			'search_subject_endpoint',
			'search_resource_endpoint',
			'search_action_endpoint'
		];
		
		for (const field of urlFields) {
			if (field in data && data[field]) {
				if (!data[field].startsWith('http://') && !data[field].startsWith('https://')) {
					throw new AuthZENValidationError(
						`Invalid metadata: ${field} must be absolute URL per spec Section 9.1.1`,
						field,
						data[field]
					);
				}
			}
		}
		
		// Validate capabilities array if present
		if ('capabilities' in data && data.capabilities !== null && data.capabilities !== undefined) {
			if (!Array.isArray(data.capabilities)) {
				throw new AuthZENValidationError(
					'Invalid metadata: capabilities must be array if present',
					'capabilities',
					data.capabilities
				);
			}
			
			// Each capability should be a string (URN)
			data.capabilities.forEach((cap: any, index: number) => {
				if (typeof cap !== 'string') {
					throw new AuthZENValidationError(
						`Invalid metadata: capabilities[${index}] must be string (URN)`,
						`capabilities[${index}]`,
						cap
					);
				}
			});
		}
		
		return data;
	}
}

